import classnames from 'classnames';
import isNull from 'lodash/isNull';

import style from '../Input.module.scss';
import '@components/mixins/commonStyles.scss';

import { Nullable } from 'chaika-frontend-types/frontend-cms';

import { FieldTitle, FieldTitleTheme } from '@components/FieldTitle';
import { IconLink, IconLinkProps } from '@components/IconLink';

import { FixedIcon } from '../../Icon';
import { NotValidFieldText } from '../../NotValidFieldText';
import { ImportantFieldIndicator } from '../../ImportantFieldIndicator';

export const InputTheme = {
    Search: style.root_search,
};

export const InputModifiers = {
    InheritBackground: style.inheritBackground,
    WithoutBottomLine: style.withoutBottomLine,
    WithoutVerticalPaddings: style.withoutVerticalPaddings,
    WithoutPaddings: style.withoutPaddings,
    InheritHeight: style.inheritHeight,
    WithLeftPadding: style.withLeftPadding,
    TransparentBottomOnView: style.transparentBottomOnView,
    NoBorder: style.noBorder,
    InheritFont: style.inheritFont,
    noWrap: style.noWrap,
    WithSmallPaddings: style.withSmallPaddings,
    WithSmallLeftPadding: style.smallLeftPadding,
    RedesignedLabel: style.redesignedLabel,
    CustomParentWrapper: style.customParentWrapper,
};

export interface InputLayoutProps {
    leftTitle?: boolean;
    title?: string;
    titleHint?: string;
    theme?: string;
    modifiers?: string[];
    notValidText?: string;
    iconLinkProps?: IconLinkProps;
    isValid?: Nullable<boolean>;
    isRequired?: boolean;
    isImportant?: boolean;
    showValidStatus?: boolean;
}

interface Props extends InputLayoutProps {
    children: React.ReactNode;
    isOnFocus: boolean;
    isEmpty: boolean;
    disabled?: boolean;
    overflowVisible?: boolean;
}

export function InputLayout({
    children,
    isOnFocus,
    isEmpty,
    disabled,
    overflowVisible,
    leftTitle,
    title,
    titleHint,
    theme,
    modifiers = [],
    notValidText,
    iconLinkProps,
    isValid = null,
    isRequired = false,
    isImportant,
    showValidStatus,
}: Props) {
    const withValidStatus = showValidStatus && !isNull(isValid);

    const stateClassNames = classnames([
        disabled && style.root_disabled,
        isOnFocus && style.root_active,
        withValidStatus && (isValid ? style.root_valid : style.root_notValid),
    ]);

    const titleText = isRequired ? `${title} *` : title;

    return (
        <div className={classnames([style.root, theme, ...modifiers, stateClassNames])}>
            {title && !leftTitle && (
                <div className={classnames([style.title, iconLinkProps && style.title_withLink])}>
                    <FieldTitle
                        theme={FieldTitleTheme.WithFlex}
                        title={
                            titleHint ? (
                                <>
                                    {titleText}
                                    <span className={style.titleHint}>{titleHint}</span>
                                </>
                            ) : (
                                titleText
                            )
                        }
                    />
                    {iconLinkProps && (
                        <div className={classnames([style.iconLink, 'font_label'])}>
                            <IconLink {...iconLinkProps} />
                        </div>
                    )}
                </div>
            )}
            <div className={classnames([style.inputWrapper, overflowVisible && style.inputWrapperOverflowVisible])}>
                {children}
                {isImportant && isEmpty && <ImportantFieldIndicator />}
            </div>
            {withValidStatus && (
                <div className={style.validationIcon}>
                    {isValid ? FixedIcon.getValidIcon() : FixedIcon.getNotValidIcon()}
                </div>
            )}

            {withValidStatus && !isValid && notValidText && <NotValidFieldText title={notValidText} />}
        </div>
    );
}
